import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Breadcrumbs, Link, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

const routeNameMap: Record<string, string> = {
    'missions': 'Mission Management',
    'content-moderation': 'Content Moderation',
    'disputes': 'Disputes Management',
    'complaints': 'Complaints Management',
    'users': 'User Management',
    'ghost-verification': 'Ghost Application Verification',
    'finance': 'Finance',
    'training': 'Training',
    'reports': 'Reports',
    'audit-log': 'Audit Log',
    'settings': 'Settings',
    'change-password': 'Change Password',
    'admin-users': 'Admin Users',
    'add': 'Add',
    'edit': 'Edit',
};

export const RouteBreadcrumbs: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const theme = useTheme();

    const paths = location.pathname.split('/').filter(Boolean);
    const breadcrumbs = [{ label: 'Home', path: '/' }];

    if (paths.length === 0) {
        breadcrumbs.push({ label: 'Dashboard Overview', path: '/' });
    } else {
        let currentPath = '';
        let prefixLabel = '';

        for (let i = 0; i < paths.length; i++) {
            const segment = paths[i];
            currentPath += `/${segment}`;

            // Check if it's a UUID or MongoDB ID (usually 24 hex chars or 36 chars with dashes)
            const isId = segment.length >= 20 || !isNaN(Number(segment));

            if (isId) {
                const label = prefixLabel ? `${prefixLabel} Details` : 'Details';
                breadcrumbs.push({ label, path: currentPath });
                prefixLabel = '';
            } else {
                if (segment === 'asset') {
                    prefixLabel = 'Asset';
                    continue; // Skip adding 'asset' segment as its own crumb
                }
                if (segment === 'edit') {
                    prefixLabel = 'Edit';
                    continue; // Skip adding 'edit' segment as its own crumb
                }

                breadcrumbs.push({
                    label: routeNameMap[segment] || segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' '),
                    path: currentPath,
                });
            }
        }
    }

    return (
        <Breadcrumbs sx={{ marginBottom: theme.spacing(2) }}>
            {breadcrumbs.map((item, index) => {
                const isLast = index === breadcrumbs.length - 1;
                if (isLast) {
                    return (
                        <Typography key={index} variant="body2" sx={{ color: theme.palette.text.primary }}>
                            {item.label}
                        </Typography>
                    );
                }
                return (
                    <Link
                        key={index}
                        component="button"
                        variant="body2"
                        onClick={() => navigate(item.path)}
                        sx={{
                            color: theme.palette.text.secondary,
                            textDecoration: 'none',
                            '&:hover': {
                                textDecoration: 'underline',
                            },
                            cursor: 'pointer',
                        }}
                    >
                        {item.label}
                    </Link>
                );
            })}
        </Breadcrumbs>
    );
};
